import React, { ReactNode, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import Button from "components/Button";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

import style from "./InputMultipleTag.module.css";

export type TInputTag = {
  name: string;
  id: string;
  image?: string;
};

interface InputMultipleTagsProps {
  values?: TInputTag[];
  keyDownActive?: boolean;
  tagValue?: string;
  loading?: boolean;
  tagBgColor?: string;
  placeHolder: string;
  rightIcon?: ReactNode;
  paddingLeft?: string;
  maxTag?: number;
  invitationLimitMessage?: string;
  validationErrorMessage?: string;
  submitLabel?: string;
  showIdValue?: boolean;
  label?: string;
  onClearInputField?: () => void;
  onRemove?: (value: number) => void;
  onFocusChange?: (isFocused: boolean) => void;
  onInputValidation?: (value: string) => boolean;
  onSubmit?: (val: TInputTag[]) => void;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSetTagValues?: (tags: TInputTag[]) => void;
  onGetTagValues?: (tags: TInputTag[]) => void;
}

const InputMultipleTags = ({
  values = [],
  keyDownActive,
  tagValue = "",
  loading,
  tagBgColor,
  placeHolder,
  rightIcon,
  paddingLeft,
  maxTag,
  invitationLimitMessage,
  validationErrorMessage,
  submitLabel,
  showIdValue = false,
  label,
  onClearInputField,
  onRemove,
  onInputValidation,
  onSubmit,
  onChange,
  onSetTagValues,
  onFocusChange,
  onGetTagValues,
}: InputMultipleTagsProps) => {
  const { t } = useTranslationContext();
  const [tag, setTag] = useState(tagValue);
  const [tags, setTags] = useState<TInputTag[]>([]);
  const [isFocused, setIsFocused] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!maxTag || tags.length < maxTag) {
      setTag(value);
      onChange && onChange(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (keyDownActive) {
      if (tag.length > 0) {
        const { key } = e;
        const newTag = tag.trim();
        if ([",", "Enter", "Tab"].includes(key)) {
          e.preventDefault();
          if (!onInputValidation || onInputValidation(newTag)) {
            const updatedTags = [...tags, { name: newTag, id: "" }];
            setTags(updatedTags);
            onSetTagValues?.(updatedTags);
            onGetTagValues?.(updatedTags);
            if (inputRef.current) {
              inputRef.current.focus();
            }
          } else {
            toast.error(validationErrorMessage || "please enter a valid email");
          }
        } else if (key === "Backspace" && !newTag.length && tags.length) {
          e.preventDefault();

          const tagsCopy = [...tags];
          const lastTag = tagsCopy.pop();

          setTags(tagsCopy);
          onSetTagValues?.(tagsCopy);

          setTag(lastTag?.name || "");
        }
      }
    }
  };

  const removeTag = (index: number) => {
    const latestTags = tags.filter((tag, i) => i !== index);
    setTags?.(latestTags);
    onSetTagValues?.(latestTags);
    onGetTagValues?.(latestTags);
    onRemove && onRemove(index);
    if (inputRef.current) {
      setTimeout(() => {
        inputRef?.current?.focus();
      }, 100);
    }
  };

  const handleSubmit = () => {
    onSubmit && onSubmit(tags);
    setTags([]);
    setTag("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleLimit = () => {
    if (tags.length === maxTag) {
      toast.error(invitationLimitMessage);
    }
  };

  useEffect(() => {
    if (values.length > 0) {
      setTags(values);
      return;
    }
    setTags([]);
  }, [values, tagValue]);

  useEffect(() => {
    setTag("");
  }, [onClearInputField]);

  useEffect(() => {
    onFocusChange?.(isFocused);
  }, [isFocused, onFocusChange]);

  return (
    <div className="p-1 flex flex-wrap items-start">
      {label && <div className="text-gray-500 text-base mb-1">{label}</div>}
      <div
        className={cn(
          "w-[100%] px-1 relative rounded-lg hover:shadow-[0px_0px_1px_1px_rgb(0,122,245,0.4)] hover:ring-primary-500 ring-primary-500",
          isFocused && "border-primary-500",
          style.tagsInputWrapper
        )}
      >
        {tags?.map((tag, index) => (
          <div
            key={index}
            className={cn(
              "rounded-[1.4rem] text-sm bg-gray-100 gap-1 justify-between flex items-center px-2 break-all",
              style.tagItem,
              tagBgColor
            )}
          >
            {showIdValue ? <div>{tag.id}</div> : <div>{t(tag.name)}</div>}
            <button
              className={cn(
                "pb-[0.2rem] text-xl rounded-r-md text-black",
                style.removeBtn
              )}
              onClick={() => removeTag(index)}
            >
              &times;
            </button>
          </div>
        ))}
        <input
          ref={inputRef}
          value={tag || tagValue}
          onChange={handleChange}
          className={cn(
            style.tagInput,
            "w-[100%] px-2 cursor-pointer min-h-[2.25rem]",
            tags.length === 0 && !isFocused && paddingLeft
          )}
          readOnly={tags.length === maxTag}
          onKeyDown={handleKeyDown}
          placeholder={tags.length === 0 && !isFocused ? placeHolder : ""}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onMouseDown={handleLimit}
        />
        {tags.length === 0 && !isFocused && (
          <span className="absolute right-3">{rightIcon}</span>
        )}
      </div>
      {onSubmit && (
        <Button
          onClick={handleSubmit}
          color="primary"
          className={"ml-2 p-2"}
          disabled={!tags?.length}
          isLoading={loading}
        >
          {submitLabel || t("ok")}
        </Button>
      )}
    </div>
  );
};

export default InputMultipleTags;
